import React, { useState, useMemo } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  User,
  ArrowRight,
  MessageSquare,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';

import {
  useGetPendingOrgChartUpdatesQuery,
  useGetOrgChartUpdateHistoryQuery,
  useActOnOrgChartUpdateMutation
} from '../../store/apis/orgChart.api';
import { ConfigurableTable, Button, SimpleTooltip, Modal } from '../common';
import type { TableColumn } from '../common/Table';
import RejectionModal from '../leave/modals/RejectionModal';
import Badge from '../common/Badge';
import { getCompOffStatusVariant } from '../../utils/badgeVariants';
import { useAuth } from '../../store/hooks/useAuth';
import { TableSkeleton } from './Skeleton';

interface PendingUpdate {
  id: string;
  userId: {
    id: string;
    fullName: string;
    employeeId: string;
    workEmail: string;
    profilePic?: string;
  };
  requesterId: {
    fullName: string;
  };
  reportingManagerId?: {
    fullName: string;
  } | null;
  functionalManagerId?: {
    fullName: string;
  } | null;
  status: string;
  workflowStatusLabel?: string;
  createdAt: string;
  decisionDate?: string;
  remarks?: string;
  isReportingUpdate?: boolean;
  isFunctionalUpdate?: boolean;
  currentActorIds?: string[];
}

const OrgChartPendingTable: React.FC = () => {
  const { user } = useAuth();
  const currentUserId = user?.id;

  const { data: requests = [], isLoading: isPendingLoading, refetch: refetchPending } = useGetPendingOrgChartUpdatesQuery();
  const { data: history = [], isLoading: isHistoryLoading } = useGetOrgChartUpdateHistoryQuery();
  const [actOnUpdate, { isLoading: isActing }] = useActOnOrgChartUpdateMutation();

  const [activeAccordion, setActiveAccordion] = useState<'pending' | 'history'>('pending');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [selectedViewRequest, setSelectedViewRequest] = useState<PendingUpdate | null>(null);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [imgError, setImgError] = useState(false);

  const handleAction = async (id: string, decision: 'approved' | 'rejected', remarks?: string) => {
    try {
      await actOnUpdate({ id, decision, remarks }).unwrap();
      toast.success(`Request ${decision} successfully`);
      refetchPending();
    } catch (error: any) {
      toast.error(error?.data?.message || `Failed to ${decision} request`);
    }
  };

  const commonColumns: TableColumn<PendingUpdate>[] = [
    {
      key: 'employee',
      title: 'Employee',
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-50 overflow-hidden flex items-center justify-center text-primary-600 font-bold text-xs shrink-0 border border-slate-100">
            {record.userId?.profilePic && !imgError ? (
              <img
                src={record.userId.profilePic}
                className="w-full h-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              record.userId?.fullName?.charAt(0) || '?'
            )}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-900 truncate">{record.userId?.fullName || 'Unknown Employee'}</div>
            <div className="text-[10px] text-slate-500 font-medium uppercase tracking-wider truncate">{record.userId?.employeeId || 'N/A'}</div>
          </div>
        </div>
      )
    },
    {
      key: 'changes',
      title: 'Requested Changes',
      render: (_, record) => {
        return (
          <div className="flex flex-col gap-1.5">
            {record.isReportingUpdate && (
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-blue-600 font-bold uppercase tracking-tight">Reporting</span>
                <ArrowRight size={12} className="text-slate-300" />
                <span className="text-slate-700 font-semibold">
                  {record.reportingManagerId?.fullName || 'Remove Manager'}
                </span>
              </div>
            )}
            {record.isFunctionalUpdate && (
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-emerald-600 font-bold uppercase tracking-tight">Functional</span>
                <ArrowRight size={12} className="text-slate-300" />
                <span className="text-slate-700 font-semibold">
                  {record.functionalManagerId?.fullName || 'Remove Manager'}
                </span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'requestedBy',
      title: 'Requested By',
      render: (_, record) => (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span>{record.requesterId?.fullName || 'System'}</span>
        </div>
      )
    },
    {
      key: 'date',
      title: 'Requested On',
      render: (_, record) => (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span>{dayjs(record.createdAt).format('DD MMM YYYY')}</span>
        </div>
      )
    }
  ];

  const pendingColumns: TableColumn<PendingUpdate>[] = [
    ...commonColumns,
    {
      key: 'status',
      title: 'Status',
      render: (_, record) => (
        <Badge variant={getCompOffStatusVariant(record.status)} size="middle">
          {record.workflowStatusLabel || record.status}
        </Badge>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, record) => (
        <div className="flex items-center gap-1">
          <SimpleTooltip label="View Details">
            <button
              onClick={() => setSelectedViewRequest(record)}
              className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4 text-primary-600" aria-hidden="true" />
            </button>
          </SimpleTooltip>
          {record.currentActorIds?.includes(currentUserId || "") && (
            <>
              <SimpleTooltip label="Approve">
                <button
                  onClick={() => handleAction(record.id, 'approved')}
                  disabled={isActing}
                  className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4 text-emerald-600" aria-hidden="true" />
                </button>
              </SimpleTooltip>
              <SimpleTooltip label="Reject">
                <button
                  onClick={() => {
                    setSelectedRequestId(record.id);
                    setRejectionReason("");
                    setIsRejectionModalOpen(true);
                  }}
                  disabled={isActing}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4 text-red-600" aria-hidden="true" />
                </button>
              </SimpleTooltip>
            </>
          )}
        </div>
      )
    }
  ];

  const historyColumns: TableColumn<PendingUpdate>[] = [
    ...commonColumns,
    {
      key: 'status',
      title: 'Status',
      render: (_, record) => (
        <Badge variant={getCompOffStatusVariant(record.status)} size="middle">
          {record.workflowStatusLabel || record.status}
        </Badge>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, record) => (
        <div className="flex items-center gap-1">
          <SimpleTooltip label="View Details">
            <button
              onClick={() => setSelectedViewRequest(record)}
              className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4 text-primary-600" aria-hidden="true" />
            </button>
          </SimpleTooltip>
        </div>
      )
    }
  ];

  const AccordionHeader: React.FC<{
    title: string;
    count: number;
    isActive: boolean;
    onClick: () => void;
    icon: React.ReactNode;
  }> = ({ title, count, isActive, onClick, icon }) => (
    <div
      onClick={onClick}
      className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${isActive ? 'bg-slate-50/50' : 'hover:bg-slate-50'
        }`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isActive ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-500'}`}>
          {icon}
        </div>
        <div>
          <span className="text-base font-bold text-slate-900">{title}</span>
          <span className="ml-2 px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-[10px] font-bold">
            {count}
          </span>
        </div>
      </div>
      {isActive ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Pending Accordion */}
      <div className="bg-white rounded-xl shadow-soft border border-slate-200">
        {activeAccordion !== "pending" ? (
          <button
            className="w-full text-left px-4 py-3 flex items-center justify-between gap-3"
            onClick={() => setActiveAccordion("pending")}
          >
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold text-slate-900">
                Pending Requests ({requests.length})
              </span>
            </div>
            <ChevronDown className="w-5 h-5 text-slate-400" />
          </button>
        ) : (
          <div className="p-0">
            <div className="border-t border-slate-100 relative">
              <ConfigurableTable
                columns={pendingColumns}
                data={requests}
                loading={isPendingLoading || isActing}
                emptyMessage="No pending reassignment requests found."
                rowKey={(record) => record.id}
                configOptions={{ persistenceKey: 'org-chart-pending-table' }}
                spacing={0}
                minHeight="150px"
                skeleton={<TableSkeleton rows={3} />}
                renderColumnSelector={(selector) => (
                  <div
                    className="w-full text-left px-4 py-3 border-b flex items-center justify-between gap-3 cursor-pointer"
                    onClick={() => setActiveAccordion("history")}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-bold text-slate-900">
                        Pending Requests ({requests.length})
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div onClick={(e) => e.stopPropagation()}>{selector}</div>
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        )}
      </div>

      {/* History Accordion */}
      <div className="bg-white rounded-xl shadow-soft border border-slate-200">
        {activeAccordion !== "history" ? (
          <button
            className="w-full text-left p-4 flex items-center justify-between"
            onClick={() => setActiveAccordion("history")}
          >
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold text-slate-900">
                Request History ({history.length})
              </span>
            </div>
            <ChevronDown className="w-5 h-5 text-slate-400" />
          </button>
        ) : (
          <div className="p-0">
            <div className="border-t border-slate-100 relative">
              <ConfigurableTable
                columns={historyColumns}
                data={history}
                loading={isHistoryLoading}
                emptyMessage="No historical requests found."
                rowKey={(record) => record.id}
                configOptions={{ persistenceKey: 'org-chart-history-table' }}
                spacing={0}
                minHeight="150px"
                skeleton={<TableSkeleton rows={3} />}
                renderColumnSelector={(selector) => (
                  <div
                    className="w-full text-left p-4 border-b flex items-center justify-between cursor-pointer"
                    onClick={() => setActiveAccordion("pending")}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-bold text-slate-900">
                        Request History ({history.length})
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div onClick={(e) => e.stopPropagation()}>{selector}</div>
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        )}
      </div>

      {isRejectionModalOpen && (
        <RejectionModal
          rejectionReason={rejectionReason}
          setRejectionReason={setRejectionReason}
          setShowRejectModal={setIsRejectionModalOpen}
          selectedLeave={selectedRequestId}
          setSelectedLeave={setSelectedRequestId}
          confirmReject={async () => {
            if (selectedRequestId) {
              await handleAction(selectedRequestId, 'rejected', rejectionReason);
              setIsRejectionModalOpen(false);
              setSelectedRequestId(null);
              setRejectionReason("");
            }
          }}
          processing={isActing ? selectedRequestId : null}
          showProcessingText={false}
          title="Reject Reassignment Request"
          description="Please provide a reason for rejecting this reassignment request."
        />
      )}

      {/* View Details Modal */}
      <Modal
        isOpen={!!selectedViewRequest}
        onClose={() => setSelectedViewRequest(null)}
        title="Request Details"
        size="lg"
      >
        {selectedViewRequest && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-y-6 gap-x-8">
              <div>
                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Employee</h4>
                <p className="text-sm font-bold text-slate-900">{selectedViewRequest.userId?.fullName}</p>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Employee ID</h4>
                <p className="text-sm font-bold text-slate-700">{selectedViewRequest.userId?.employeeId || '—'}</p>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Requested By</h4>
                <p className="text-sm font-bold text-slate-900">{selectedViewRequest.requesterId?.fullName}</p>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Request Date</h4>
                <p className="text-sm font-bold text-slate-700">{dayjs(selectedViewRequest.createdAt).format('DD MMM YYYY')}</p>
              </div>
              {selectedViewRequest.remarks && (
                <div className="col-span-2">
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Decision Remarks</h4>
                  <p className="text-sm font-medium text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-100">{selectedViewRequest.remarks}</p>
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-3">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Requested Changes</h4>
              <div className="space-y-3">
                {selectedViewRequest.isReportingUpdate && (
                  <div className="text-sm leading-relaxed text-slate-700">
                    This request is for assigning <span className="font-bold text-blue-600">Reporting Manager</span> of <span className="font-bold text-slate-900">{selectedViewRequest.userId?.fullName}</span> to <span className="font-bold text-slate-900">{selectedViewRequest.reportingManagerId?.fullName || 'None (Remove Link)'}</span>.
                  </div>
                )}
                {selectedViewRequest.isFunctionalUpdate && (
                  <div className="text-sm leading-relaxed text-slate-700">
                    This request is for assigning <span className="font-bold text-emerald-600">Functional Manager</span> of <span className="font-bold text-slate-900">{selectedViewRequest.userId?.fullName}</span> to <span className="font-bold text-slate-900">{selectedViewRequest.functionalManagerId?.fullName || 'None (Remove Link)'}</span>.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrgChartPendingTable;
